import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "../../common/components/Grid";
import ReactForm from "../../common/components/ReactForm";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { canInsertDocType, canRemoveDocType, createDocType, removeDocType, useAllDocTypes } from "../../reducers/docTypeSlice";
import GroupedSettings from "./GroupedSettings";
import PropTypes from "prop-types";

const defaultSort = ['resource'];

const newDocTypeTemplate = {};

const DocTypeDetails = forwardRef(({ value: doctype, groupCtrl, disabled = false, onChange }, formRef) => {
    const { t } = useTranslation();
    return <ReactForm ref={formRef}>
        <Row>
            <Col>
                <TextInput placeholder={t('name')} readonly={doctype.internalid} value={doctype.name} onChange={value => onChange('name')(value)} disabled={disabled} required></TextInput>
            </Col>
        </Row>
        {groupCtrl}
    </ReactForm>
});
DocTypeDetails.propTypes = {
    value: PropTypes.object,
    groupCtrl: PropTypes.any,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
}

const DocTypes = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const doctypes = useAllDocTypes();
    const sortFields = useMemo(() => ({
        resource: t('name')
    }), [t]);
    const groupingCtrls = useMemo(() => {
        const ctrls = [{ type: 'type', label: t('type', { count: 1 }), title: t('type', { count: 1 }), field: ['site','level','type'], sortfield: 'type', wildcard: t('anyWildcard'), strict: false }]
        if (clientConfig.enableLevels) {
            ctrls.unshift({ type: 'level', label: t('level', { count: 1 }), title: t('level', { count: 1 }), field: ['site','level','type'], sortfield: 'level', wildcard: t('anyWildcard'), strict: false });
        }
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site','level','type'], sortfield: 'site', wildcard: t('anyWildcard'), strict: false });
        }
        return ctrls;
    }, [clientConfig.enableLevels, clientConfig.enableSites, t]);
    const onAdd = useCallback(doctype => {
        delete doctype.internalid;
        return createDocType(network, doctype)
    }, [network]);
    const onRemove = useCallback(doctype => {
        return removeDocType(network, doctype)
    }, [network]);

    return <GroupedSettings Details={DocTypeDetails} data={doctypes} uuid={uuid} title={label}
            defaultEntry={newDocTypeTemplate} defaultSort={defaultSort} sortFields={sortFields}
            onClose={onClose} makeLabel={e => e.name}
            initialGroupingField="name"
            groupingExclusions={['site', 'level', 'type', 'internalid']}
            canAdd={entry => canInsertDocType(credentials, entry)} canUpdate={entry => false} canRemove={entry => canRemoveDocType(credentials, entry)}
            onAdd={onAdd} onRemove={onRemove}
            groupingCtrls={groupingCtrls}></GroupedSettings>
}
DocTypes.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default DocTypes;